import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Carousel } from '../../components/Carousel';
import { BossCard } from '../boss/BossCard';
import { spriteFor } from '../boss/sprites';

// Wall-TV surface for boss fights, decoupled from the broader
// public board. Same data shape as the public board's
// `adventure.bosses` array — uses `/api/public-board` and pulls
// only the bosses out so the rest of the panels stay on
// /public-board.
//
// v0.8.9: infinite single-line carousel, 3 cards visible at a
// time on the wall TV, scrolling left one card at a time on a
// loop. Reuses the shared Carousel component (which handles the
// overflow-detection + duplicate-copy + step-width math). When
// 3 or fewer bosses are active the carousel renders a single
// copy with no scroll animation.

interface Boss {
  id: string; name: string; hp: number | null; damage_dealt: number;
  ends_at: string;
  status: 'draft' | 'active' | 'vanquished' | 'survived' | 'archived';
  tickets_total?: number; tickets_closed?: number;
  users?: Array<{ user_id: string; handle: string; name: string | null; color?: string | null; damage?: number }>;
}

interface BoardResponse {
  adventure?: {
    bosses?: Boss[];
  };
}

export function BossFightsPage() {
  const { data, isLoading } = useQuery<BoardResponse>({
    queryKey: ['public-board'],
    queryFn: () => api<BoardResponse>('/api/public-board'),
    refetchInterval: 5_000,
  });
  const bosses = data?.adventure?.bosses ?? [];

  return (
    <div className="fixed inset-0 flex flex-col bg-bg text-ink">
      <header className="flex items-center gap-3 px-8 py-4 border-b border-white/10">
        <span
          className="grid h-9 w-9 place-items-center rounded-md text-bg text-xl font-bold"
          style={{ backgroundImage: 'linear-gradient(135deg, var(--fantasy-gold) 0%, #2a1f0a 100%)' }}
        >
          M
        </span>
        <span className="text-2xl font-semibold">Boss Fights</span>
      </header>
      <main
        className="flex-1 flex items-center justify-center px-8 py-6 min-h-0 overflow-hidden"
        style={{ fontSize: 'clamp(0.875rem, 0.8vw, 1.4rem)' }}
      >
        {isLoading ? (
          <div className="text-ink-muted mx-auto">Loading bosses…</div>
        ) : bosses.length === 0 ? (
          <div className="text-ink-muted mx-auto">No active bosses.</div>
        ) : (
          <Carousel className="pb-1" holdMs={4000} perView={3} stepPx={288}>
            {bosses.map((b) => (
              <BossCard
                key={b.id}
                boss={{
                  id: b.id,
                  name: b.name,
                  hp: b.hp ?? 0,
                  damage_dealt: b.damage_dealt,
                  status: (b.status === 'archived' ? 'survived' : b.status) as 'active' | 'vanquished' | 'survived',
                  ends_at: b.ends_at,
                  tickets_total: b.tickets_total,
                  tickets_closed: b.tickets_closed,
                  users: (b.users ?? []).map((u) => ({
                    user_id: u.user_id,
                    handle: u.handle,
                    name: u.name,
                    damage: u.damage ?? 0,
                    color: u.color ?? null,
                  })),
                }}
                sprite={spriteFor(b.name)}
                variant="full"
                size="lg"
                engine="3d"
              />
            ))}
          </Carousel>
        )}
      </main>
    </div>
  );
}
